import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { getMangaBySlug, categories } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = getMangaBySlug(slug || '');

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${manga.title}-漫画全集下拉式免费阅读-土豪漫画网`;
  const pageDesc = `${manga.title}漫画全集下拉式免费阅读，土豪漫画网提供${manga.title}全集中文漫画在线下拉式免费阅读服务。作者：${manga.author}，${manga.description}`;
  const pageKeywords = `${manga.title},漫画全集下拉式免费阅读,土豪漫画网`;

  const cat = categories.find(c => c.name === manga.category);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: manga.category, path: cat ? `/category/${cat.slug}` : '/' },
    { label: manga.title },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://i2p1d.com/" },
      { "@type": "ListItem", "position": 2, "name": manga.category, "item": cat ? `http://i2p1d.com/category/${cat.slug}` : "http://i2p1d.com/" },
      { "@type": "ListItem", "position": 3, "name": manga.title },
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": manga.title,
    "description": manga.description,
    "author": { "@type": "Person", "name": manga.author },
    "datePublished": manga.chapters[0]?.updateTime,
    "dateModified": manga.updateTime,
    "image": manga.cover,
    "publisher": { "@type": "Organization", "name": "土豪漫画网" }
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": pageDesc,
    "url": `http://i2p1d.com/manga/${slug}`,
    "isPartOf": { "@type": "WebSite", "name": "土豪漫画网", "url": "http://i2p1d.com/" },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://i2p1d.com/" },
        { "@type": "ListItem", "position": 2, "name": manga.category, "item": cat ? `http://i2p1d.com/category/${cat.slug}` : "http://i2p1d.com/" },
        { "@type": "ListItem", "position": 3, "name": manga.title },
      ]
    }
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`http://i2p1d.com/manga/${slug}`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogImage={manga.cover}
        ogType="article"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />

      <Navbar />

      <main className="pt-20 pb-14">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-72 flex-shrink-0">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={manga.cover}
                  alt={`${manga.title}漫画封面`}
                  title={manga.title}
                  className="w-full aspect-[3/4] object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">{manga.title}</h1>
              <p className="text-foreground-500 text-sm mb-4">作者：{manga.author}</p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {manga.tags.map((tag) => (
                  <span key={tag} className="bg-secondary-100 text-secondary-700 text-xs px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                <span className="bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full text-xs">{manga.category}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  manga.updateStatus === '已完结' ? 'bg-secondary-200 text-secondary-700' : 'bg-primary-100 text-primary-700'
                }`}>
                  {manga.updateStatus}
                </span>
                <span className="flex items-center"><i className="ri-eye-line mr-1"></i>{(manga.views / 10000).toFixed(1)}万次阅读</span>
                <span className="flex items-center"><i className="ri-star-fill text-primary-500 mr-1"></i>{manga.rating}分</span>
              </div>

              <p className="text-foreground-600 text-sm leading-relaxed mb-6">{manga.description}</p>

              <div className="flex items-center gap-3">
                {manga.chapters.length > 0 && (
                  <Link
                    to={`/manga/${manga.slug}/chapter/${manga.chapters[0].id}`}
                    className="bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm px-6 py-2.5 rounded-full whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2"
                  >
                    <i className="ri-play-fill"></i>
                    从第一话开始阅读
                  </Link>
                )}
                <Link
                  to={`/manga/${manga.slug}/chapter/${manga.chapters[manga.chapters.length - 1].id}`}
                  className="border border-foreground-300 hover:border-primary-500 text-foreground-700 hover:text-primary-600 text-sm px-6 py-2.5 rounded-full whitespace-nowrap cursor-pointer transition-colors"
                >
                  最新话：{manga.chapters[manga.chapters.length - 1].title}
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
              章节列表 <span className="text-foreground-400 text-base font-normal">（共{manga.chapters.length}话）</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {manga.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/manga/${manga.slug}/chapter/${chapter.id}`}
                  className="bg-background-50 hover:bg-background-100 border border-background-200/70 rounded-xl px-4 py-3 text-sm transition-colors cursor-pointer"
                >
                  <p className="font-medium text-foreground-900 truncate">{chapter.title}</p>
                  <p className="text-xs text-foreground-400 mt-1">
                    <i className="ri-eye-line mr-1"></i>{(chapter.views / 10000).toFixed(1)}万
                  </p>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-12 p-6 bg-background-100 rounded-2xl">
            <h3 className="text-lg font-bold text-foreground-900 mb-2">阅读提示</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              本作品在土豪漫画网提供<strong>漫画全集下拉式免费阅读</strong>服务。点击任意章节即可进入下拉式阅读模式，无需频繁翻页，
              流畅浏览完整章节内容。所有漫画内容均为中文版本，支持手机和电脑在线免费阅读。
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}